import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IsaleModal } from "./Modal";
import { useTranslation } from "@/shared/i18n/I18nProvider";

/**
 * CostCapModal — global upgrade UX for two distinct quota blocks:
 *   - 402 QUOTA_COST_CAP_REACHED (F-Q1): monthly AI cost cap spent; the
 *     offending pre-render AI call was refused server-side BEFORE any spend.
 *     Listens to `isale:cost-cap-reached`.
 *   - 429 QUOTA_EXCEEDED (CF-M3): monthly video / AI-mode cap reached — the
 *     most common render block. Listens to `isale:quota-exceeded` and shows
 *     the "out of videos this month → upgrade" copy instead of the cost-cap
 *     wording.
 * Both events are dispatched by apiRequest (apps/isale-web/src/shared/api/client.ts).
 *
 * Mounted once in App.tsx inside <BrowserRouter> so useNavigate works. Web
 * parity of mobile UpgradePromptModal (quota | cost_cap) — shares the
 * modals.costCap* / modals.upgrade* i18n copy (vi/en/zh).
 */
export function CostCapModal() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [kind, setKind] = useState<"quota" | "cost_cap">("cost_cap");

  useEffect(() => {
    function onCostCap() {
      setKind("cost_cap");
      setVisible(true);
    }
    function onQuota() {
      setKind("quota");
      setVisible(true);
    }
    window.addEventListener("isale:cost-cap-reached", onCostCap);
    window.addEventListener("isale:quota-exceeded", onQuota);
    return () => {
      window.removeEventListener("isale:cost-cap-reached", onCostCap);
      window.removeEventListener("isale:quota-exceeded", onQuota);
    };
  }, []);

  const isQuota = kind === "quota";

  return (
    <IsaleModal
      visible={visible}
      onDismiss={() => setVisible(false)}
      title={t(isQuota ? "modals.upgradeTitle" : "modals.costCapTitle")}
      subtitle={t(isQuota ? "modals.upgradeBody" : "modals.costCapBody")}
      variant="warning"
      actions={[
        {
          label: t(isQuota ? "modals.upgradeCta" : "modals.costCapCta"),
          variant: "primary",
          onPress: () => {
            setVisible(false);
            navigate("/settings/billing");
          },
        },
        {
          label: t(isQuota ? "modals.upgradeLater" : "modals.costCapLater"),
          variant: "secondary",
          onPress: () => setVisible(false),
        },
      ]}
    />
  );
}
